import React, { useEffect, useState } from "react";
import { db, auth } from "./firebase"; 
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import toast, { Toaster } from "react-hot-toast";
import "./UsersPanel.css";

const UsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  // --- MODAL STATE ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // --- FORM STATE ---
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    status: "",
    domain: "",
    hosting: "",
    url: "",
    revenue: "",
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setCurrentUserRole(userDoc.data().role);
        }
      }
    });

    fetchUsers();
    return () => unsubscribe();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersCollectionRef = collection(db, "users");
      
      /** * UPDATED QUERY: 
       * We filter out both 'admin' and 'superadmin' roles.
       * If your users have a role like 'user' or 'client', it's often safer to 
       * query specifically for that role to avoid showing any management accounts.
       */
      const q = query(
        usersCollectionRef, 
        where("role", "not-in", ["admin", "superadmin"])
      );

      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching users: ", error);
      toast.error("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  // --- DELETE FEATURE ---
  const handleDelete = async (userId, userName) => {
    if (currentUserRole !== "superadmin") {
      alert("Unauthorized: Only Superadmins can delete users.");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete ${userName || "this user"}? This action cannot be undone.`
    );

    if (confirmDelete) {
      try {
        const userRef = doc(db, "users", userId);
        await deleteDoc(userRef);

        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
        toast.success("User deleted permanently.");
      } catch (error) {
        console.error("Error deleting user: ", error);
        toast.error("Failed to delete user.");
      }
    }
  };

  // --- OPEN MODAL & PREFILL DATA ---
  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || "",
      email: user.email || "",
      status: user.status || "in progress",
      domain: user.domain || "",
      hosting: user.hosting || "",
      url: user.url || "",
      revenue: user.revenue || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const userRef = doc(db, "users", selectedUser.id);
      await updateDoc(userRef, formData);

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === selectedUser.id ? { ...user, ...formData } : user
        )
      );

      toast.success("User updated successfully!");
      closeModal();
    } catch (error) {
      console.error("Error updating user: ", error);
      toast.error("Failed to update user.");
    }
  };

  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: "5px 10px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: "bold",
      textTransform: "uppercase",
    };
    switch (status) {
      case "active":
        return { ...baseStyle, backgroundColor: "#d1fae5", color: "#065f46" };
      case "inactive":
        return { ...baseStyle, backgroundColor: "#fee2e2", color: "#991b1b" };
      default:
        return { ...baseStyle, backgroundColor: "#fef3c7", color: "#92400e" };
    }
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
    marginTop: "20px",
    backgroundColor: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
    border: "1px solid #eee",
  };

  const thTdStyle = {
    textAlign: "left",
    padding: "16px",
    borderBottom: "1px solid #eee",
  };

  return (
    <div
      className="card"
      style={{
        padding: "20px",
        backgroundColor: "#f9fafb",
        borderRadius: "12px",
      }}
    >
      <Toaster />
      <h2
        style={{
          margin: "0 0 20px 0",
          color: "#111827",
          fontSize: "20px",
          fontWeight: "700",
        }}
      >
        User Management
      </h2>

      {loading ? (
        <p>Loading users...</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <th style={{ ...thTdStyle, color: "#6b7280", fontSize: "12px", textTransform: "uppercase" }}>Name</th>
              <th style={{ ...thTdStyle, color: "#6b7280", fontSize: "12px", textTransform: "uppercase" }}>Email</th>
              <th style={{ ...thTdStyle, color: "#6b7280", fontSize: "12px", textTransform: "uppercase" }}>Subdomain</th>
              <th style={{ ...thTdStyle, color: "#6b7280", fontSize: "12px", textTransform: "uppercase" }}>Status</th>
              <th style={{ ...thTdStyle, color: "#6b7280", fontSize: "12px", textTransform: "uppercase" }}>Website</th>
              <th style={{ ...thTdStyle, color: "#6b7280", fontSize: "12px", textTransform: "uppercase", textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="table-row-hover">
                <td style={{ ...thTdStyle, fontWeight: "500", color: "#111827" }}>{user.name || "N/A"}</td>
                <td style={{ ...thTdStyle, color: "#6b7280" }}>{user.email || "N/A"}</td>
                <td style={{ ...thTdStyle }}>
                  {user.domain ? (
                    <code style={{ backgroundColor: "#f3f4f6", padding: "2px 6px", borderRadius: "4px", fontSize: "12px" }}>
                      {user.domain}
                    </code>
                  ) : (
                    <span style={{ color: "#9ca3af", fontStyle: "italic" }}>None</span>
                  )}
                </td>
                <td style={thTdStyle}>
                  <span style={getStatusBadgeStyle(user.status)}>
                    {user.status || "in progress"}
                  </span>
                </td>
                <td style={thTdStyle}>
                  {user.url ? (
                    <a href={user.url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", textDecoration: "none", fontWeight: "500" }}>
                      View
                    </a>
                  ) : (
                    "N/A"
                  )}
                </td>
                <td style={{ ...thTdStyle, display: "flex", gap: "8px", justifyContent: "center" }}>
                  <button className="modern-edit-btn" onClick={() => openEditModal(user)}>
                    ✏️ Edit
                  </button>

                  {/* --- CONDITIONAL DELETE BUTTON --- */}
                  {currentUserRole === "superadmin" && (
                    <button
                      className="modern-delete-btn"
                      onClick={() => handleDelete(user.id, user.name)}
                    >
                      🗑️ Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* --- MODAL POPUP --- */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit User: {formData.name}</h3>
              <button className="close-modal" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} />
                </div>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange} className="modern-select">
                  <option value="in progress">In Progress</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Domain Name</label>
                  <input type="text" name="domain" value={formData.domain} onChange={handleInputChange} placeholder="example.com" />
                </div>
                <div className="form-group">
                  <label>Hosting Name</label>
                  <input type="text" name="hosting" value={formData.hosting} onChange={handleInputChange} placeholder="AWS, GoDaddy" />
                </div>
              </div>
              <div className="form-group">
                <label>Website URL</label>
                <input type="url" name="url" value={formData.url} onChange={handleInputChange} placeholder="https://..." />
              </div>
              <div className="form-group">
                <label>Revenue ($)</label>
                <input type="number" name="revenue" value={formData.revenue} onChange={handleInputChange} placeholder="0.00" />
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="save-btn">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPanel;